// Shared tooltip-text builder for calendar absence blocks. Used by
// month-view, year-view and the hour-grid day-strip so the hover hint is
// consistent everywhere — type + status + notes + substitute + approval.
//
// Kept tiny and dependency-free so any view can `import` it without
// pulling component-level state.
import type { Absence, AbsenceType } from './api'

const TYPE_LABEL: Record<AbsenceType, string> = {
  vacation: 'Urlaub',
  sick: 'Krankmeldung',
  training: 'Fortbildung',
  other: 'Abwesenheit',
  overtime_reduction: 'Überstundenabbau',
  maternity: 'Mutterschutz',
  parental: 'Elternzeit',
}

const STATUS_LABEL: Record<string, string> = {
  pending: 'Ausstehend',
  approved: 'Genehmigt',
  rejected: 'Abgelehnt',
}

function fmtDate(d: string | Date): string {
  const date = d instanceof Date ? d : new Date(d)
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Build a multi-line tooltip describing the absence. Lines joined by '\n'
 * so the browser's native tooltip renders them; works in title attributes
 * across all major browsers. Returns '' when no absence is given. */
export function absenceTooltip(absence: Absence | null | undefined): string {
  if (!absence) return ''
  const lines: string[] = []

  // Header: type + status badge inline
  const status = STATUS_LABEL[absence.status] ?? absence.status
  lines.push(`${TYPE_LABEL[absence.type] ?? absence.type} (${status})`)

  // Range
  const from = fmtDate(absence.fromDate)
  const to = fmtDate(absence.toDate)
  lines.push(from === to ? from : `${from} – ${to}`)

  // Notes
  if (absence.notes?.trim()) {
    lines.push(`Notiz: ${absence.notes.trim()}`)
  }

  // Substitute (iteration 2 schema — typed loosely so we don't depend on
  // a not-yet-merged field on the frontend Absence interface).
  type WithMaybeSubstitute = {
    substituteUserName?: string | null
    substituteName?: string | null
    substituteUserId?: string | null
  }
  const sub = absence as unknown as WithMaybeSubstitute
  const subName = sub.substituteUserName ?? sub.substituteName ?? null
  if (subName) {
    lines.push(`Vertretung: ${subName}`)
  } else if (sub.substituteUserId) {
    lines.push('Vertretung: gesetzt')
  }

  // Approval info
  if (absence.status === 'approved' && (absence.reviewerName || absence.reviewedAt)) {
    const by = absence.reviewerName ? ` durch ${absence.reviewerName}` : ''
    const at = absence.reviewedAt ? ` am ${fmtDate(absence.reviewedAt)}` : ''
    lines.push(`Genehmigt${by}${at}`.trim())
  }
  if (absence.status === 'rejected' && absence.reviewNote) {
    lines.push(`Begründung: ${absence.reviewNote}`)
  }

  // Cancellation note (Task 12)
  if (absence.cancellationRequested) {
    lines.push('Stornierung beantragt')
  }

  return lines.join('\n')
}
